import { ChevronsUpDown, LogOut, Moon, Sun } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { logoutFromCognito } from '@/lib/auth'
import { initials, roleLabel } from '@/lib/labels'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/auth'
import { useUIStore } from '@/store/ui'

const ROW =
  'flex h-10 w-full items-center gap-2 rounded-md px-2.5 text-sm text-popover-foreground outline-none hover:bg-muted focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-ring max-sm:min-h-11'

/**
 * Account card in the sidebar footer. A disclosure (not an ARIA menu): two buttons reached by Tab,
 * closed by Escape or a click outside. Compact mode is the icon rail, where only the initials fit.
 */
export default function ProfileMenu({ compact }: { compact: boolean }) {
  const user = useAuthStore((s) => s.user)
  const { darkMode, setDarkMode } = useUIStore()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()
  const email = user?.email || 'User'

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault() // inside the phone drawer, Escape must close this popover only, not the drawer too
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={compact ? `Account, ${email}` : undefined}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex items-center gap-3 rounded-lg text-left outline-none hover:bg-sidebar-hover focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-sidebar-ring',
          compact ? 'size-11 justify-center' : 'w-full border border-sidebar-border px-3 py-2.5',
        )}
      >
        <span aria-hidden className="grid size-9 shrink-0 place-items-center rounded-full bg-sidebar-foreground text-sm font-semibold text-sidebar">
          {initials(user?.email)}
        </span>
        {compact ? null : (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-sidebar-foreground">{email}</span>
              <span className="block truncate text-xs text-sidebar-label">{roleLabel(user?.role)}</span>
            </span>
            <ChevronsUpDown className="size-4 shrink-0 text-sidebar-label" aria-hidden />
          </>
        )}
      </button>

      {open ? (
        <div
          id={panelId}
          className={cn(
            // Solid card: the glass budget is already spent on the top bar and dialogs.
            'absolute z-10 rounded-lg border border-border-strong bg-popover p-1 shadow-lg',
            compact ? 'bottom-0 left-full ml-2 w-52' : 'right-0 bottom-full left-0 mb-2',
          )}
        >
          <button type="button" role="switch" aria-checked={darkMode} onClick={() => setDarkMode(!darkMode)} className={ROW}>
            {darkMode ? <Sun className="size-4" aria-hidden /> : <Moon className="size-4" aria-hidden />}
            Dark theme
            {/* The track shows the state; the icon alone read as the action, not the setting. */}
            <span aria-hidden className={cn('ml-auto h-5 w-9 shrink-0 rounded-full p-0.5 transition-colors', darkMode ? 'bg-primary' : 'bg-input')}>
              <span className={cn('block size-4 rounded-full bg-card transition-transform motion-reduce:transition-none', darkMode && 'translate-x-4')} />
            </span>
          </button>
          <button type="button" onClick={() => void logoutFromCognito()} className={ROW}>
            <LogOut className="size-4" aria-hidden />
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  )
}
